/**
 * PageHeader — the fixed header shape at the top of every full page.
 *
 * Anatomy: h1 + optional description + optional right-side action slot.
 *
 * Deliberately icon-less: the left nav rail already carries each tab's icon, so
 * repeating it beside the page title was redundant decoration. No uppercase
 * eyebrows above the title either (DESIGN.md anti-pattern).
 */
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  action,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <header
      className={cn('mb-6 flex flex-wrap items-end justify-between gap-4', className)}
    >
      <div className="min-w-0">
        <h1 className="text-xl font-medium tracking-tight text-ink">{title}</h1>
        {description ? (
          <p className="mt-1 text-sm text-ink-muted [text-wrap:pretty]">{description}</p>
        ) : null}
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </header>
  );
}
